import { createContext, useContext, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmCtx = createContext(null);

/**
 * useConfirm() returns an async confirm({title, description, confirmText, cancelText, destructive}) -> Promise<boolean>
 * Nothing is deleted until the user actively confirms.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "This action cannot be undone easily.",
        confirmText: opts.confirmText || "Delete",
        cancelText: opts.cancelText || "Keep it",
        destructive: opts.destructive !== false,
        resolve,
      });
    });
  }, []);

  const close = (result) => {
    setState((s) => {
      if (s.resolve) s.resolve(result);
      return { open: false };
    });
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent
          data-testid="confirm-dialog"
          className="rounded-3xl border-0"
          style={{ background: "var(--bg-elev)", boxShadow: "var(--sh-lift)", maxWidth: 460 }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle asChild>
              <div className="font-serif text-2xl font-semibold" style={{ color: "var(--ink)" }} data-testid="confirm-title">
                {state.title}
              </div>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }} data-testid="confirm-desc">
                {state.description}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-2 flex-row justify-end">
            <AlertDialogCancel
              data-testid="confirm-cancel"
              onClick={() => close(false)}
              className="btn btn-ghost mt-0"
              style={{ borderRadius: "var(--r-pill)" }}
            >
              {state.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-accept"
              onClick={() => close(true)}
              className={state.destructive ? "btn btn-accent" : "btn btn-accent"}
              style={
                state.destructive
                  ? { background: "#b83a2b", color: "#fff", borderColor: "#b83a2b", borderRadius: "var(--r-pill)" }
                  : { borderRadius: "var(--r-pill)" }
              }
            >
              {state.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmCtx.Provider>
  );
}
